import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { Dialog, DialogTitle } from '@mui/material';

import {
    clearClaimEvents,
    loadParticipantByPtCode,
    searchClaimEvents,
    updateClaimQueue
} from '../../../store/participantService';
import { getNameFromId } from '../../../utils/directusFunctions';
import Button from '../../formElements/Button';
import SingleSelect from '../../formElements/SingleSelect';
import TextAreaField from '../../formElements/TextAreaField';

import { NOTE_CHAR_LIMIT, validate } from './validateStatusEditor';

import form from '../../../commonStyles/formStyles.module.css';
import modal from '../../../commonStyles/modal.module.css';

const StatusEditor = ({
    open,
    onClose,
    isDisabled,
    statusRow,
    claimEventStatuses,
    claimEventStatusReasons
}) => {
    // HOOKS
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        id: '',
        statusId: '',
        reasonId: '',
        notes: ''
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [isOpen, setIsOpen] = React.useState(false);
    const [filteredClaimEventStatuses, setFilteredClaimEventStatuses] = useState([]);
    const keys = {
        statusId: '0',
        reasonId: '1'
    };

    // STORE STATE
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const loadingCurrentParticipant = useSelector(
        (state) => state.entities.participantService.loadingCurrentParticipant
    );
    const claimEvent = useSelector((state) => state.entities.participantService.claimEvent);

    // USE EFFECTS
    useEffect(() => {
        if (!('ptCode' in statusRow) || loadingCurrentParticipant) return;
        if (!currentParticipant?.ptCode || statusRow.ptCode !== currentParticipant?.ptCode) {
            dispatch(loadParticipantByPtCode(statusRow.ptCode));
        } else if (currentParticipant.eligibilityGroupId) {
            dispatch(
                searchClaimEvents({
                    eligibilityGroupIds: [currentParticipant.eligibilityGroupId],
                    claimTypeId: statusRow.eventTypeId
                })
            );
        }
    }, [statusRow?.ptCode, currentParticipant?.ptCode, loadingCurrentParticipant]);

    useEffect(() => {
        let updatedEventStatuses = [];
        if (Object.keys(claimEvent)?.length) {
            const eventStatus = claimEvent.claimTypeStatuses.find(
                (entry) => entry.statusId === statusRow.statusId
            );
            if (eventStatus) {
                updatedEventStatuses = claimEventStatuses.filter((el) =>
                    eventStatus.claimTypeToStatusIds?.some((entry) => entry === el.id)
                );
            }
        }
        setFilteredClaimEventStatuses(updatedEventStatuses);
    }, [claimEvent]);

    useEffect(() => {
        setIsOpen(open);
    }, [open]);

    // HELPER FNS
    const cleanUp = () => {
        dispatch(clearClaimEvents());
        onClose();
    };

    const clearError = (key) => {
        setErrors((prev) => ({ ...prev, [key]: { ...errors[key], error: false } }));
    };

    // EVENT HANDLERS
    const onInputUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onStatusUpdate = (value) => {
        clearError('statusId');
        'reasonId' in errors && clearError('reasonId');
        setNewEntry((prev) => ({ ...prev, statusId: value, reasonId: '' }));
    };

    const onCancel = () => {
        cleanUp();
    };

    const onSubmit = (e) => {
        e.preventDefault();
        const newErrors = validate(newEntry, claimEventStatuses);
        if (Object.keys(newErrors).length) {
            setErrors(newErrors);
            return;
        }
        const payload = { ...newEntry, id: statusRow.id };
        dispatch(updateClaimQueue(payload));
        cleanUp();
    };

    // RENDER
    return (
        <div>
            <Dialog
                open={isOpen}
                onClose={onCancel}
                aria-labelledby="form-dialog-title"
                fullWidth={true}>
                <div className={modal.modalHeader}>
                    <DialogTitle id="form-dialog-title">Update Status</DialogTitle>
                </div>
                <div className={modal.modalContent}>
                    <form
                        onSubmit={onSubmit}
                        className={form.formWrapper}
                        data-testid="update_status_form_start">
                        <SingleSelect
                            id={'statusId'}
                            key={keys.statusId}
                            label={'Status'}
                            placeholder="Select Status"
                            mandatory={true}
                            disabled={isDisabled}
                            menuItems={filteredClaimEventStatuses || []}
                            selectedId={newEntry.statusId || ''}
                            selected={
                                filteredClaimEventStatuses.find(
                                    (el) => el.id === newEntry?.statusId
                                ) || {}
                            }
                            error={errors.statusId}
                            onChange={(chosenId) => onStatusUpdate(chosenId)}
                        />
                        {getNameFromId(claimEventStatuses, newEntry.statusId).toLowerCase() ===
                            'fail' && (
                            <SingleSelect
                                id={'reasonId'}
                                key={keys.reasonId}
                                label={'Reason'}
                                placeholder="Select Reason"
                                mandatory={!isDisabled}
                                disabled={isDisabled}
                                menuItems={claimEventStatusReasons || []}
                                selectedId={newEntry.reasonId || ''}
                                selected={
                                    claimEventStatusReasons.find(
                                        (el) => el.id === newEntry?.reasonId
                                    ) || {}
                                }
                                error={errors.reasonId}
                                onChange={(chosenId) => onInputUpdate('reasonId', chosenId)}
                            />
                        )}
                        <TextAreaField
                            id="notes"
                            label="Notes"
                            placeholder={'Reversion notes'}
                            disabled={isDisabled}
                            mandatory={true}
                            maxLength={NOTE_CHAR_LIMIT}
                            rows={5}
                            value={newEntry.notes || ''}
                            count={`${newEntry.notes.length}/${NOTE_CHAR_LIMIT}`}
                            error={errors.notes}
                            onChange={(e) => onInputUpdate('notes', e.target.value)}
                        />

                        <div className={modal.actionButtons}>
                            <Button
                                id="statusEditorUpdateButton"
                                disabled={isDisabled}
                                content="Update"
                            />
                            <Button
                                id="statusEditorCancelButton"
                                type="button"
                                disabled={isDisabled}
                                content="Cancel"
                                onClick={onCancel}
                            />
                        </div>
                    </form>
                </div>
            </Dialog>
        </div>
    );
};

StatusEditor.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func,
    isDisabled: PropTypes.bool,
    statusRow: PropTypes.object,
    claimEventStatuses: PropTypes.array,
    claimEventStatusReasons: PropTypes.array
};

export default StatusEditor;
